import _ from 'lodash';
import { format, setISODay } from 'date-fns';
import update from 'immutability-helper';
import reduceReducers from 'reduce-reducers';
import { toast } from 'qw-ui-lib-v2';
import { validateSchema, isStringEmpty } from '../common/validations';
import qwPartners from '../common/clients/qwPartners';
import I18n from '../../config/i18n';
import monarch from '../common/clients/monarch';
import { showAppBusy, handleAppError } from './app';
import {
  initializeUserAccount,
  loadAccountData,
  loadUserInfo,
} from './account';

const initialState = {
  phones: [],
  emails: [],
  timezone: null,
  businessHours: [
    ...[1, 2, 3, 4, 5, 6, 7].map((dayOrdinal) =>
      Object.assign(
        {},
        {
          dayOrdinal: dayOrdinal,
          dayName: format(setISODay(new Date(), dayOrdinal), 'EEEE'),
          startTime: null,
          endTime: null,
        },
      ),
    ),
  ],
  errors: {},
  receivedAt: Date.now(),
};

const CLEAR_COMPANY_DETAILS = 'companyDetails/CLEAR_COMPANY_DETAILS';
const RECEIVE = 'companyDetails/RECEIVE';
const ADD_PHONE = 'companyDetails/ADD_PHONE';
const UPDATE_PHONE = 'companyDetails/UPDATE_PHONE';
const DELETE_PHONE = 'companyDetails/DELETE_PHONE';
const ADD_EMAIL = 'companyDetails/ADD_EMAIL';
const UPDATE_EMAIL = 'companyDetails/UPDATE_EMAIL';
const DELETE_EMAIL = 'companyDetails/DELETE_EMAIL';
const UPDATE_TIMEZONE = 'companyDetails/UPDATE_TIMEZONE';
const UPDATE_BUSINESS_HOURS = 'companyDetails/UPDATE_BUSINESS_HOURS';
const UPDATE_BUSINESS_HOUR_START_TIME =
  'companyDetails/UPDATE_BUSINESS_HOUR_START_TIME';
const UPDATE_BUSINESS_HOUR_END_TIME =
  'companyDetails/UPDATE_BUSINESS_HOUR_END_TIME';
const EXPAND_BUSINESS_HOUR = 'companyDetails/EXPAND_BUSINESS_HOUR';
const SCHEMA_ID = 'http://partners.qubewire.com/schemas/updateCompanyDetails';

const ACCOUNT_FIELDS = ['phones', 'emails', 'timezone', 'businessHours'];

export function receiveCompanyDetails(account) {
  const companyDetails = _.pick(account, ACCOUNT_FIELDS);
  if (_.isEmpty(companyDetails)) {
    return;
  }
  return { type: RECEIVE, payload: companyDetails };
}

export function deleteCompanyDetails() {
  return { type: CLEAR_COMPANY_DETAILS };
}

export function saveCompanyDetails(partnerId) {
  return (dispatch, getState) => {
    let { companyDetails } = getState();
    let data = _.pick(companyDetails, ACCOUNT_FIELDS);
    return monarch
      .post(`/v1/partners/accounts/${partnerId}`, data)
      .then(() =>
        toast.add({
          message: I18n.t('companyDetailsSaveSuccess'),
          variant: 'positive',
          placement: 'top-right',
        }),
      )
      .catch(() =>
        toast.add({
          message: I18n.t('failedToSaveCompanyDetails'),
          variant: 'negative',
          placement: 'top-right',
        }),
      );
  };
}

export function updateCompanyDetails(partnerOrg) {
  return (dispatch, getState) => {
    let { companyDetails, certifications } = getState();

    let data = companyDetails;
    _.assign(data, { certifications });
    dispatch(showAppBusy(true));

    return qwPartners
      .put(`/v1/partner/${partnerOrg}`, data)
      .then(() => {
        return dispatch(loadUserInfo()).then((res) => {
          let user = _.omit(res.data, 'approvals');

          dispatch(initializeUserAccount(user));
          dispatch(loadAccountData());
          dispatch(showAppBusy(false));
          toast.add({
            message: I18n.t('companyDetailsSaveSuccess'),
            variant: 'positive',
            placement: 'top-right',
          });
        });
      })
      .catch((error) => {
        dispatch(showAppBusy(false));
        dispatch(handleAppError(error, I18n.t('failedToSaveCompanyDetails')));
      });
  };
}

export function updateCompanyPhone(payload, meta) {
  if (isPhoneEmpty(payload)) {
    return deleteCompanyPhone(meta);
  } else {
    return { type: UPDATE_PHONE, payload, meta };
  }
}

export function deleteCompanyPhone(meta) {
  return { type: DELETE_PHONE, meta };
}

export function addCompanyPhone(payload) {
  if (isPhoneEmpty(payload)) {
    return { type: null };
  } else {
    return { type: ADD_PHONE, payload };
  }
}

export function updateCompanyEmail(payload, meta) {
  if (isEmailEmpty(payload)) {
    return deleteCompanyEmail(meta);
  } else {
    return { type: UPDATE_EMAIL, payload, meta };
  }
}

export function deleteCompanyEmail(meta) {
  return { type: DELETE_EMAIL, meta };
}

export function addCompanyEmail(payload) {
  if (isEmailEmpty(payload)) {
    return { type: null };
  } else {
    return { type: ADD_EMAIL, payload };
  }
}

export function updateCompanyBusinessHourStartTime(startTime, index) {
  return {
    type: UPDATE_BUSINESS_HOUR_START_TIME,
    payload: startTime,
    meta: index,
  };
}

export function updateCompanyBusinessHourEndTime(endTime, index) {
  return {
    type: UPDATE_BUSINESS_HOUR_END_TIME,
    payload: endTime,
    meta: index,
  };
}

export function updateCompanyBusinessHours(payload) {
  return { type: UPDATE_BUSINESS_HOURS, payload };
}

export function expandCompanyBusinessHours() {
  return { type: EXPAND_BUSINESS_HOUR };
}

export function updateCompanyTimezone(payload) {
  return { type: UPDATE_TIMEZONE, payload };
}

function isPhoneEmpty(contact) {
  return (
    !contact ||
    (isStringEmpty(contact.intlCode) &&
      isStringEmpty(contact.phoneNo) &&
      isStringEmpty(contact.category))
  );
}

function isEmailEmpty(contact) {
  return !contact || isStringEmpty(contact.email);
}

function reducer(state = initialState, action) {
  let { type, payload, meta } = action;
  switch (type) {
    case RECEIVE:
      return Object.assign({}, initialState, payload, {
        receivedAt: Date.now(),
      });

    case ADD_PHONE:
      return update(state, { phones: { $push: [payload] } });

    case UPDATE_PHONE:
      return update(state, { phones: { $splice: [[meta, 1, payload]] } });

    case DELETE_PHONE:
      return update(state, { phones: { $splice: [[meta, 1]] } });

    case ADD_EMAIL:
      return update(state, { emails: { $push: [payload] } });

    case UPDATE_EMAIL:
      return update(state, { emails: { $splice: [[meta, 1, payload]] } });

    case DELETE_EMAIL:
      return update(state, { emails: { $splice: [[meta, 1]] } });

    case UPDATE_TIMEZONE:
      return update(state, { timezone: { $set: payload } });

    case UPDATE_BUSINESS_HOUR_START_TIME:
      return update(state, {
        businessHours: { [meta]: { startTime: { $set: payload } } },
      });

    case UPDATE_BUSINESS_HOUR_END_TIME:
      return update(state, {
        businessHours: { [meta]: { endTime: { $set: payload } } },
      });

    case EXPAND_BUSINESS_HOUR:
      return Object.assign({}, state, {
        businessHours: [
          ...[1, 2, 3, 4, 5].map((dayOrdinal) =>
            Object.assign({}, state.businessHours[0], {
              dayOrdinal: dayOrdinal,
              dayName: format(setISODay(new Date(), dayOrdinal), 'EEEE'),
            }),
          ),
          ...[6, 7].map((dayOrdinal) =>
            Object.assign({
              dayOrdinal: dayOrdinal,
              dayName: format(setISODay(new Date(), dayOrdinal), 'EEEE'),
              startTime: null,
              endTime: null,
            }),
          ),
        ],
      });
    case UPDATE_BUSINESS_HOURS:
      return update(state, { businessHours: { $set: payload } });

    case CLEAR_COMPANY_DETAILS:
      return update(state, { $set: initialState });

    default:
      return state;
  }
}

function validator(state, action) {
  let { type } = action;
  switch (type) {
    case RECEIVE:
    case ADD_PHONE:
    case UPDATE_PHONE:
    case DELETE_PHONE:
    case ADD_EMAIL:
    case UPDATE_EMAIL:
    case DELETE_EMAIL:
    case UPDATE_TIMEZONE:
    case UPDATE_BUSINESS_HOUR_START_TIME:
    case UPDATE_BUSINESS_HOUR_END_TIME:
    case EXPAND_BUSINESS_HOUR:
    case UPDATE_BUSINESS_HOURS: {
      let errors = validateSchema(state, SCHEMA_ID);
      return Object.assign({}, state, { errors });
    }
    default:
      return state;
  }
}

export default reduceReducers(reducer, validator);
